import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import PageableTable, {
  TableHeaderMobileHidden,
  TableHeaderMobileOnly,
} from 'src/components/pageable-table';
import { PullRequest as PullRequestShape } from 'src/types/pull-requests-table';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { useCycleTime } from './cycle-time/hooks/use-cycle-time';
import { useStale } from './cycle-time/hooks/use-stale';
import PullRequestRow from './pull-request-row-container';
import { PullRequestsTableStateVisibility } from './pull-requests-table-types';
import messages from './pull-requests-table.i18n';
import * as styles from './pull-requests-table.style';

export type PullRequestsTableProps = {
  emptyState?: JSX.Element;
  isError?: boolean;
  Heading: JSX.Element;
  pullRequests: PullRequestShape[];
  showAvatar?: boolean;
  showRepository?: boolean;
  showState?: PullRequestsTableStateVisibility;
  focusedRowIndex?: number | null;
  onLoadPage?: () => void;
  isLoading?: boolean;
  size?: styles.PullRequestsTableSize;
  renderPagination?: (() => JSX.Element) | null;
  exception?: Error;
};

export const PullRequestsTable = (
  props: PullRequestsTableProps
): JSX.Element => {
  const {
    Heading,
    emptyState,
    focusedRowIndex,
    onLoadPage,
    renderPagination,
    pullRequests = [],
    showAvatar = false,
    showRepository = false,
    showState = PullRequestsTableStateVisibility.None,
    isError = false,
    isLoading = false,
    size = styles.PullRequestsTableSize.Medium,
    exception,
  } = props;

  const colSpans = styles.COL_SPANS_WITH_AGE;

  const { cycleTimeMetricsMap } = useCycleTime(
    pullRequests.length > 0,
    pullRequests
  );

  const staleMap = useStale(pullRequests, cycleTimeMetricsMap);

  // This will be used to pass the sentry ID to the error state later
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sentryId, setSentryId] = React.useState<string>('');

  useEffect(() => {
    (async () => {
      if (isError && exception) {
        const id = await captureExceptionWithTags(exception, {
          visibleToUser: 'false',
        });
        setSentryId(id);
      }
    })();
  }, [exception, isError]);

  const renderHeaders = () => {
    return (
      <tr>
        <TableHeaderMobileHidden scope="col" colSpan={colSpans[size].summary}>
          {Heading}
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" colSpan={colSpans[size].age}>
          <FormattedMessage {...messages.tableHeaderCreated} />
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" colSpan={colSpans[size].activity}>
          <FormattedMessage {...messages.tableHeaderActivity} />
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" colSpan={colSpans[size].reviewers}>
          <FormattedMessage {...messages.tableHeaderReviewers} />
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" colSpan={colSpans[size].builds}>
          <styles.BuildStatusTableHeader>
            <FormattedMessage {...messages.tableHeaderBuilds} />
          </styles.BuildStatusTableHeader>
        </TableHeaderMobileHidden>
        <TableHeaderMobileOnly
          scope="col"
          colSpan={colSpans[size].mobileSummary}
        >
          {Heading}
        </TableHeaderMobileOnly>
        <TableHeaderMobileOnly
          scope="col"
          colSpan={colSpans[size].mobileBuilds}
        >
          <styles.BuildStatusTableHeader>
            <FormattedMessage {...messages.tableHeaderBuilds} />
          </styles.BuildStatusTableHeader>
        </TableHeaderMobileOnly>
      </tr>
    );
  };

  const renderEmptyState = () => {
    return (
      <tr>
        <td colSpan={colSpans[size].total}>{emptyState}</td>
      </tr>
    );
  };

  return (
    <PageableTable
      renderEmptyState={renderEmptyState}
      isLoading={isLoading}
      isError={isError}
      onClickErrorState={onLoadPage}
      renderHeaders={renderHeaders}
      renderPagination={renderPagination}
      rows={pullRequests}
    >
      {(pullRequest, index) => (
        <PullRequestRow
          key={`${pullRequest.links.html.href}`}
          pullRequest={pullRequest}
          showAvatar={showAvatar}
          showRepository={showRepository}
          showState={showState}
          isFocused={index === focusedRowIndex}
          isLoading={isLoading}
          size={size}
          position={index + 1}
          cycleTimeMetrics={
            cycleTimeMetricsMap[pullRequest.destination.repository.uuid]
          }
          stale={staleMap[pullRequest.id]}
        />
      )}
    </PageableTable>
  );
};

export default PullRequestsTable;
